import { getPricingRules, getGlobalRules } from "@/api/pricingRules";
import { rulesEnum } from "@/util/enums/rules";

const rules = {
  namespaced: true,
  state: {
    currentTab: "",
    globalRules: {},
    costAdditionRules: [],
    bioCostAdditionRules: [],
    temperatureCostAdditionRules: [],
    customerMarginRules: [],
    rulesToExcludeFromCalculation: [],
    ruleValidationThreshold: {
      MinTemperatureCelsius: 0,
      MaxTemperatureCelsius: 32,
    },
    eRuleExclusionType: {
      Removed: "REMOVED",
      Overridden: "OVERRIDDEN",
      Reset: "RESET",
    },
    isRuleRemovalConfirmed: false,
    deletedRuleScope: null,
    costAdditionRulesPaged: [],
    bioCostAdditionRulesPaged: [],
    temperatureCostAdditionRulesPaged: [],
    customerMarginRulesPaged: [],
    pagedData: {},
    defaultPagedMetadata: {
      pageCount: 0,
      pageLimit: 25,
      pageNumber: 1,
      total: 0,
    },
    filters: {
      COST_ADDITIONS: {},
      BIO_COST_ADDITIONS: {},
      TEMPERATURE: {},
      CUSTOMER: {},
    },
  },
  getters: {
    getCurrentTab: function (state) {
      return state.currentTab;
    },
    globalRulesGetter: function (state) {
      return state.globalRules;
    },
    ruleValidationThreshold: function (state) {
      return state.ruleValidationThreshold;
    },
    eRuleExclusionType: function (state) {
      return state.eRuleExclusionType;
    },
    isRuleRemovalConfirmed: function (state) {
      return state.isRuleRemovalConfirmed;
    },
    deletedRuleScope: function (state) {
      return state.deletedRuleScope;
    },
    costAdditionRulesPagedGetter: function (state) {
      return state.costAdditionRulesPaged;
    },
    bioCostAdditionRulesPagedGetter: function (state) {
      return state.bioCostAdditionRulesPaged;
    },
    temperatureCostAdditionRulesPagedGetter: function (state) {
      return state.temperatureCostAdditionRulesPaged;
    },
    customerMarginRulesPagedGetter: function (state) {
      return state.customerMarginRulesPaged;
    },
    getPageMetadata: function (state) {
      return state.pagedData;
    },
    // Due to current vuex reactivity issues, the next 3 getters must return the top level filter object for current tab
    getSelectedTerminalFilter(state) {
      return state.filters[state.currentTab];
    },
    getSelectedNetworkFilter(state) {
      return state.filters[state.currentTab];
    },
    getSelectedProductFilter(state) {
      return state.filters[state.currentTab];
    },
    getNameFilter(state) {
      return state.filters[state.currentTab];
    },
  },
  actions: {
    setCurrentTab: function ({ commit }, payload) {
      commit("SET_CURRENT_TAB", payload);
    },
    getGlobalRules: async function ({ commit }) {
      await getGlobalRules().then((globalRules) => {
        commit("SET_GLOBAL_RULES", globalRules);
      });
    },
    getGlobalSpecifiedRulesPaged: async function ({ commit, getters }, payload) {
      const name = getters["getNameFilter"] && getters["getNameFilter"].name;
      const terminalIds =
        getters["getSelectedTerminalFilter"] &&
        getters["getSelectedTerminalFilter"].terminals &&
        getters["getSelectedTerminalFilter"].terminals.map((t) => t._id);
      const networkIds =
        getters["getSelectedNetworkFilter"] &&
        getters["getSelectedNetworkFilter"].networks &&
        getters["getSelectedNetworkFilter"].networks.map((t) => t._id);
      const productIds =
        getters["getSelectedProductFilter"] &&
        getters["getSelectedProductFilter"].products &&
        getters["getSelectedProductFilter"].products.map((p) => p._id);
      await getPricingRules({
        pageType: payload.pageType,
        pageLimit: Number(payload.pageLimit),
        pageNumber: Number(payload.pageNumber),
        priority: payload.priority,
        name,
        terminalIds,
        networkIds,
        productIds,
      }).then((costAdditionRules) => {
        switch (payload.pageType) {
          case "COST_ADDITIONS": {
            commit("SET_COST_ADDITION_RULES_PAGED", costAdditionRules);
            break;
          }
          case "BIO_COST_ADDITIONS": {
            commit("SET_COST_ADDITION_RULES_BIO_PAGED", costAdditionRules);
            break;
          }
          case "TEMPERATURE": {
            commit("SET_COST_ADDITION_RULES_TEMP_PAGED", costAdditionRules);
            break;
          }
          case "CUSTOMER": {
            commit("SET_COST_ADDITION_RULES_CUSTOMER_PAGED", costAdditionRules);
            break;
          }
        }
      });
    },
    confirmRuleRemoval: function ({ commit }, isConfirmed) {
      commit("CONFIRM_RULE_REMOVAL", isConfirmed);
    },
    setDeletedRuleScope: function ({ commit }, ruleScope) {
      commit("SET_DELETED_RULE_SCOPE", ruleScope);
    },
    setTerminalFilter: function ({ commit }, payload) {
      commit("SET_TERMINAL_FILTER", payload);
    },
    setNetworkFilter: function ({ commit }, payload) {
      commit("SET_NETWORK_FILTER", payload);
    },
    setProductFilter: function ({ commit }, payload) {
      commit("SET_PRODUCT_FILTER", payload);
    },
    setNameFilter: function ({ commit }, payload) {
      commit("SET_NAME_FILTER", payload);
    },
  },
  mutations: {
    SET_CURRENT_TAB: function (state, payload) {
      state.currentTab = payload;
    },
    SET_GLOBAL_RULES: function (state, globalRules) {
      // Let's ensure that values are displayed with correct decimal places first
      for (let rule of globalRules.dailySettlementRules) {
        const { targetAttribute, value } = rule.action;
        rule.action.value = value
          ? Number(value).toFixed(targetAttribute === rulesEnum.eTargetAttributeType.Fx ? 4 : 2)
          : "";
      }

      for (let rule of [].concat.apply([], Object.values(globalRules.productRules))) {
        const { value } = rule.action;
        rule.action.value = Number(value).toFixed(2);
      }

      let iceLivePremiumRule = globalRules.iceDataRules.find(
        (rule) => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.IceLivePremium
      );
      iceLivePremiumRule.action.value = iceLivePremiumRule.action.value
        ? Number(iceLivePremiumRule.action.value).toFixed(2)
        : "";

      // Now, we can assign the updated values
      state.globalRules = globalRules;
    },
    SET_COST_ADDITION_RULES_PAGED: function (state, payload) {
      state.costAdditionRulesPaged = payload.rules || [];
      state.pagedData = payload.metadata || state.defaultPagedMetadata;
    },
    SET_COST_ADDITION_RULES_BIO_PAGED: function (state, payload) {
      state.bioCostAdditionRulesPaged = payload.rules || [];
      state.pagedData = payload.metadata || state.defaultPagedMetadata;
    },
    SET_COST_ADDITION_RULES_TEMP_PAGED: function (state, payload) {
      state.temperatureCostAdditionRulesPaged = payload.rules || [];
      state.pagedData = payload.metadata ? payload.metadata : state.defaultPagedMetadata;
    },
    SET_COST_ADDITION_RULES_CUSTOMER_PAGED: function (state, payload) {
      state.customerMarginRulesPaged = payload.rules || [];
      state.pagedData = payload.metadata ? payload.metadata : state.defaultPagedMetadata;
    },
    CONFIRM_RULE_REMOVAL: function (state, isConfirmed) {
      state.isRuleRemovalConfirmed = isConfirmed;
    },
    SET_DELETED_RULE_SCOPE: function (state, ruleScope) {
      state.deletedRuleScope = ruleScope;
    },
    SET_TERMINAL_FILTER: (state, payload) => {
      if (!state.filters[state.currentTab]) {
        state.filters[state.currentTab] = {};
      }
      state.filters[state.currentTab].terminals = payload;
    },
    SET_NETWORK_FILTER: (state, payload) => {
      if (!state.filters[state.currentTab]) {
        state.filters[state.currentTab] = {};
      }
      state.filters[state.currentTab].networks = payload;
    },
    SET_PRODUCT_FILTER: (state, payload) => {
      if (!state.filters[state.currentTab]) {
        state.filters[state.currentTab] = {};
      }
      state.filters[state.currentTab].products = payload;
    },
    SET_NAME_FILTER: (state, payload) => {
      state.filters[state.currentTab].name = payload;
    },
  },
};

export default rules;
